import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Radar, Flame, ThumbsUp, ThumbsDown, Plus, Loader2, ExternalLink, PlayCircle,
} from "lucide-react";

const REASONS = [
  ["great_fit", "Great fit"],
  ["wrong_industry", "Wrong industry"],
  ["too_large", "Too large"],
  ["too_small", "Too small"],
  ["no_buying_intent", "No buying intent"],
  ["wrong_person", "Wrong person"],
  ["spam", "Spam"],
  ["duplicate", "Duplicate"],
  ["strong_commission_intent", "Strong commission intent"],
  ["converted", "Converted"],
  ["other", "Other"],
];

export default function GrowthOpportunities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLane, setFilterLane] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ source_url: "", snippet: "", company: "", location: "" });
  const [addSaving, setAddSaving] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(null);
  const [feedbackReason, setFeedbackReason] = useState("great_fit");
  const [running, setRunning] = useState(false);
  const [serperRunning, setSerperRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterLane !== "all") params.lane = filterLane;
      const { data } = await api.get("/growth/opportunities", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterLane]);

  useEffect(() => { load(); }, [load]);

  const submitAdd = async () => {
    if (!addForm.source_url.trim() || addForm.snippet.trim().length < 4) {
      return toast.error("Source URL and snippet are required.");
    }
    setAddSaving(true);
    try {
      await api.post("/growth/opportunities", {
        source_url: addForm.source_url.trim(),
        snippet: addForm.snippet.trim(),
        company: addForm.company.trim() || null,
        location: addForm.location.trim() || null,
        lane: "manual_paste",
      });
      toast.success("Opportunity added — AI scored it.");
      setAddOpen(false);
      setAddForm({ source_url: "", snippet: "", company: "", location: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAddSaving(false);
    }
  };

  const submitFeedback = async (verdict) => {
    try {
      await api.post(`/growth/opportunities/${feedbackOpen.id}/feedback`, {
        verdict, reason: feedbackReason,
      });
      toast.success(verdict === "up" ? "Thanks — scoring will remember this." : "Dismissed.");
      setFeedbackOpen(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const runJsearch = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/growth/lanes/jsearch/run");
      if (data.skipped) {
        toast.warning(`JSearch skipped: ${data.skipped}`);
      } else {
        toast.success(`JSearch ran — ${data.inserted} new, ${data.duplicates} dupes.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const runSerper = async () => {
    setSerperRunning(true);
    try {
      const { data } = await api.post("/growth/lanes/serper/run");
      if (data.skipped) {
        toast.warning(`Serper skipped: ${data.skipped}. Enable the lane and set SERPER_API_KEY in Settings.`);
      } else {
        toast.success(`Serper ran — ${data.inserted} new, ${data.duplicates} dupes.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSerperRunning(false);
    }
  };

  const convert = async (opp) => {
    try {
      const { data } = await api.post(`/growth/opportunities/${opp.id}/convert`, {});
      toast.success(data.deduped ? "Deduped to existing lead." : "Converted to lead.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-7xl mx-auto" data-testid="growth-opportunities">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2">
            <Radar className="h-7 w-7 text-indigo-400" /> Opportunity Radar
          </h1>
          <p className="text-slate-400 text-sm mt-1">Scored prospects from every enabled discovery lane. Thumbs-down teaches scoring.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runJsearch} disabled={running} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold" data-testid="run-jsearch-btn">
            {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1" />}
            Run JSearch
          </Button>
          <Button onClick={runSerper} disabled={serperRunning} className="bg-violet-500 hover:bg-violet-400 text-slate-950 font-extrabold" data-testid="run-serper-btn">
            {serperRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1" />}
            Run Serper
          </Button>
          <Button onClick={() => setAddOpen(true)} className="bg-indigo-600 hover:bg-indigo-500" data-testid="add-opp-btn">
            <Plus className="h-4 w-4 mr-1" /> Add opportunity
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44 bg-slate-900 border-slate-700 text-slate-200" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="discovered">Discovered</SelectItem>
            <SelectItem value="needs_review">Needs human review</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterLane} onValueChange={setFilterLane}>
          <SelectTrigger className="w-44 bg-slate-900 border-slate-700 text-slate-200" data-testid="filter-lane">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All lanes</SelectItem>
            <SelectItem value="jsearch">JSearch</SelectItem>
            <SelectItem value="serper">Serper</SelectItem>
            <SelectItem value="manual_paste">Manual paste</SelectItem>
            <SelectItem value="google_alerts">Google Alerts</SelectItem>
            <SelectItem value="youtube">YouTube</SelectItem>
            <SelectItem value="competitor_reviews">Competitor reviews</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-slate-400 flex items-center gap-2 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-10 text-center text-slate-400">
            <Radar className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm">No opportunities yet. Run a lane or paste one manually.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((o) => (
            <Card key={o.id} className="bg-slate-900/60 border-slate-800 hover:border-indigo-500/40 transition-colors" data-testid={`opp-row-${o.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 rounded px-1.5 py-0.5">{o.lane}</span>
                      {o.priority_score >= 0.7 && <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest text-amber-300"><Flame className="h-3 w-3" /> High intent</span>}
                      {o.status === "needs_review" && <span className="text-[10px] font-extrabold uppercase tracking-widest text-rose-300">Needs review</span>}
                      {o.converted_lead_id && <span className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-300">Converted</span>}
                    </div>
                    <p className="text-white font-bold mt-2 truncate">{o.company || "Unknown company"}</p>
                    <p className="text-slate-300 text-sm mt-1 line-clamp-2">{o.snippet}</p>
                    {o.detected_keywords?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">kw: {o.detected_keywords.join(" · ")}</p>
                    )}
                    <a href={o.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1 mt-1.5">
                      {o.source_url.slice(0, 72)} <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="shrink-0 text-right space-y-2">
                    <div className="flex items-center justify-end gap-3 text-xs text-slate-400">
                      <span>intent <b className="text-white">{Math.round((o.intent_score ?? 0) * 100)}</b></span>
                      <span>fit <b className="text-white">{Math.round((o.fit_score ?? 0) * 100)}</b></span>
                    </div>
                    <p className="text-2xl font-extrabold text-indigo-300">{Math.round((o.priority_score ?? 0) * 100)}</p>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => { setFeedbackOpen({ ...o, _verdict: "up" }); setFeedbackReason("great_fit"); }} className="h-7 w-7 text-emerald-300 hover:bg-emerald-500/10" data-testid={`thumbs-up-${o.id}`}>
                        <ThumbsUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => { setFeedbackOpen({ ...o, _verdict: "down" }); setFeedbackReason("wrong_industry"); }} className="h-7 w-7 text-rose-300 hover:bg-rose-500/10" data-testid={`thumbs-down-${o.id}`}>
                        <ThumbsDown className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {!o.converted_lead_id && o.status !== "dismissed" && (
                      <Button size="sm" variant="outline" onClick={() => convert(o)} className="h-7 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 text-xs" data-testid={`convert-${o.id}`}>
                        → Lead
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="add-opp-dialog">
          <DialogHeader>
            <DialogTitle>Add opportunity</DialogTitle>
            <DialogDescription className="text-slate-400">Paste a URL + a snippet of the signal (job posting, forum comment, review). AI scores and classifies.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Source URL</Label>
              <Input value={addForm.source_url} onChange={(e) => setAddForm({ ...addForm, source_url: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="add-opp-url" />
            </div>
            <div>
              <Label>Snippet (what signals intent)</Label>
              <Textarea rows={4} value={addForm.snippet} onChange={(e) => setAddForm({ ...addForm, snippet: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="add-opp-snippet" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Company (optional)</Label>
                <Input value={addForm.company} onChange={(e) => setAddForm({ ...addForm, company: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="add-opp-company" />
              </div>
              <div>
                <Label>Location (optional)</Label>
                <Input value={addForm.location} onChange={(e) => setAddForm({ ...addForm, location: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="add-opp-location" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-slate-700 bg-slate-800">Cancel</Button>
            <Button onClick={submitAdd} disabled={addSaving} className="bg-indigo-600 hover:bg-indigo-500" data-testid="add-opp-submit">
              {addSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Score & add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback dialog */}
      <Dialog open={!!feedbackOpen} onOpenChange={(o) => !o && setFeedbackOpen(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle>{feedbackOpen?._verdict === "up" ? "Great fit — teach the scorer" : "Why dismiss?"}</DialogTitle>
            <DialogDescription className="text-slate-400">Feedback is persisted forever and used to tune lane scoring.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Select value={feedbackReason} onValueChange={setFeedbackReason}>
              <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map(([code, label]) => <SelectItem key={code} value={code}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackOpen(null)} className="border-slate-700 bg-slate-800">Cancel</Button>
            <Button onClick={() => submitFeedback(feedbackOpen._verdict)} className={feedbackOpen?._verdict === "up" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"} data-testid="feedback-submit">
              Save feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
